// RubberBandPicker — the candidate sheet that appears after the user
// drags a flight-plan leg to a new map location.
//
// Shows up to four kinds of insert candidates:
//
//   1. The raw GPS coordinate they dropped on (always available)
//   2. Nearest airport(s)
//   3. Nearest fix(es)
//   4. Nearest navaid(s) (VOR / VORTAC / NDB)
//
// Tapping any row inserts that candidate into the route at
// `legIndex + 1` so it sits between the original leg's endpoints.
// ForeFlight's rubber-band UX, simplified.
//
// Anchored near the drop point with viewport clamping. Closes on Esc
// or outside-tap. Same affordance as PickList.

import { useEffect, useRef } from 'react';
import type { PlanPoint } from '../stores/planStore';

export interface RubberBandCandidate extends PlanPoint {
  /** Optional human-readable name (rendered as the row subtitle). */
  name?: string;
  /** Distance in nautical miles from the drop point. Hidden when omitted. */
  distNm?: number;
  /** Tag for the row dot. "gps" -> ember, "airport" -> ember, "fix" -> violet, "navaid" -> cyan. */
  rowKind: 'gps' | 'airport' | 'fix' | 'navaid';
}

interface Props {
  candidates: RubberBandCandidate[];
  /** Drop point in canvas-relative pixels. */
  point: { x: number; y: number };
  onPick: (c: RubberBandCandidate) => void;
  onClose: () => void;
  loading?: boolean;
}

const KIND_DOT: Record<RubberBandCandidate['rowKind'], string> = {
  gps: 'bg-ember-500',
  airport: 'bg-ember-500',
  fix: 'bg-violet-400',
  navaid: 'bg-cyan-400',
};

const KIND_LABEL: Record<RubberBandCandidate['rowKind'], string> = {
  gps: 'GPS',
  airport: 'APT',
  fix: 'FIX',
  navaid: 'NAV',
};

export default function RubberBandPicker({
  candidates, point, onPick, onClose, loading,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    function onDocPointer(e: PointerEvent) {
      const node = ref.current;
      if (!node || node.contains(e.target as Node)) return;
      onClose();
    }
    window.addEventListener('keydown', onKey);
    // Defer so the same gesture that opened the sheet doesn't close it.
    const t = window.setTimeout(() => {
      window.addEventListener('pointerdown', onDocPointer, true);
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDocPointer, true);
    };
  }, [onClose]);

  // Anchor: bias up-and-right of the drop, clamp to viewport.
  const W = 260;
  const maxH = Math.min(360, candidates.length * 56 + 80);
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = point.x + 14;
  if (left + W + margin > vw) left = Math.max(margin, point.x - W - 14);
  let top = point.y - 8;
  if (top + maxH + margin > vh) top = Math.max(margin, vh - maxH - margin);
  if (top < margin) top = margin;

  return (
    <div
      ref={ref}
      style={{ position: 'absolute', left, top, width: W }}
      className="z-30 bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-rail overflow-hidden"
      role="dialog"
      aria-label="Insert waypoint into leg"
    >
      <div className="px-3 py-2 border-b border-cockpit-700 flex items-center justify-between">
        <span className="text-[11px] tracking-widest text-cockpit-300">
          INSERT WAYPOINT
        </span>
        <button
          onClick={onClose}
          aria-label="Cancel"
          className="text-cockpit-300 hover:text-cockpit-100 text-sm leading-none"
        >✕</button>
      </div>
      {loading && (
        <div className="px-3 py-3 text-[11px] text-cockpit-400">Looking nearby…</div>
      )}
      {!loading && candidates.length === 0 && (
        <div className="px-3 py-3 text-[11px] text-cockpit-400">
          Nothing nearby. Try dragging closer to a feature.
        </div>
      )}
      <ul className="max-h-72 overflow-y-auto scroll-touch divide-y divide-cockpit-700">
        {candidates.map((c, i) => (
          <li key={`${c.rowKind}-${c.ident}-${i}`}>
            <button
              type="button"
              onClick={() => onPick(c)}
              className="w-full text-left px-3 py-2 hover:bg-cockpit-700 active:bg-cockpit-700 flex items-center gap-2"
            >
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${KIND_DOT[c.rowKind]} shrink-0`}
                aria-hidden
              />
              <span className="flex flex-col min-w-0 flex-1">
                <span className="font-mono text-sm text-cockpit-100 truncate">
                  {c.ident}
                </span>
                <span className="text-[11px] text-cockpit-300 truncate">
                  {c.name || ''}
                  {c.name && c.distNm != null ? ' · ' : ''}
                  {c.distNm != null ? `${c.distNm} nm` : ''}
                </span>
              </span>
              <span className="text-[10px] tracking-widest text-cockpit-400 shrink-0">
                {KIND_LABEL[c.rowKind]}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
